import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, TrendingUp, DollarSign, Clock, RefreshCw, Filter, Package, Search, CreditCard as CardIcon, DollarSign as CashIcon, ChevronDown, Download, Heart, Users } from 'lucide-react';
import { localOrderDataService, LocalOrder } from '../../services/localOrderData';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import { getProductCategory, getAllCategories, ProductCategory } from '../../utils/productCategories';
import { apiService } from '../../services/api';

const SALA_DE_JUNTAS_REGEX = /sala de juntas/i;

interface SalaDeJuntasStats {
  salaRevenue: number;
  otherRevenue: number;
  bookings: number;
  tickets: number;
  firstDate: string | null;
}

const ClipTransactions: React.FC = () => {
  const [orders, setOrders] = useState<LocalOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<LocalOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: '',
    to: ''
  });
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'card' | 'cash'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showProducts, setShowProducts] = useState<string | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory | 'all'>('all');
  const [sortBy, setSortBy] = useState<'revenue' | 'quantity'>('revenue');
  const [useMongoData, setUseMongoData] = useState(true); // Toggle for data source
  const [employeeFilter, setEmployeeFilter] = useState<'exclude' | 'include' | 'only'>('exclude');
  const [extractingProducts, setExtractingProducts] = useState(false);
  const [processingDeductions, setProcessingDeductions] = useState(false);
  const [deductionResult, setDeductionResult] = useState<any>(null);
  const [tipFilter, setTipFilter] = useState<'all' | 'withTips' | 'onlyTips'>('all');
  const [salaFilter, setSalaFilter] = useState<'all' | 'only' | 'exclude'>('all');
  const [salaStats, setSalaStats] = useState<SalaDeJuntasStats | null>(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      let allOrders: LocalOrder[] = [];
      
      if (useMongoData) {
        // Fetch from MongoDB via Netlify function
        try {
          const mongoTransactions = await apiService.getTransactions({ limit: 5000 });
          
          // Convert MongoDB format to LocalOrder format
          allOrders = mongoTransactions.map((txn: any, idx: number) => {
            const products = (txn.products || []).map((p: any) => ({
              name: p.name || p.producto || '',
              quantity: p.quantity || p.cantidad || 1,
              price: p.price || p.unitPrice || p.precio || 0,
              total: p.total || p.totalPrice || (p.price || p.unitPrice || 0) * (p.quantity || 1),
            }));
            const hasProducts = txn.hasDetailedData || (txn.products && txn.products.length > 0) || false;
            // Compute tip: amount - sum of product totals (only for orders with products)
            let tip = 0;
            if (hasProducts && products.length > 0) {
              const productsTotal = products.reduce((sum: number, p: any) => sum + p.total, 0);
              const diff = txn.amount - productsTotal;
              // Only count as tip if difference is reasonable (< 30% of order)
              // Larger gaps likely mean missing products, not a real tip
              if (diff > 0.5 && diff / txn.amount < 0.30) {
                tip = Math.round(diff * 100) / 100;
              }
            }
            // Use explicit tip from MongoDB if available
            if (txn.tip != null && txn.tip > 0) {
              tip = txn.tip;
            }
            return {
              index: idx,
              orderName: txn.orderName || txn.reference || txn.clipId,
              amount: txn.amount,
              date: txn.transactionDate || txn.createdAt,
              dateTimeText: txn.transactionDate || txn.createdAt || '',
              paymentMethod: (txn.paymentMethod === 'cash' ? 'cash' : 'card') as 'card' | 'cash',
              status: txn.status || 'Completado',
              orderNumber: txn.clipId || txn._id || String(idx),
              products,
              hasDetailedData: hasProducts,
              tip,
            };
          });
          
          console.log(`✅ Loaded ${allOrders.length} transactions from MongoDB`);
        } catch (mongoError) {
          console.error('Error fetching from MongoDB, falling back to local data:', mongoError);
          // Fallback to local data if MongoDB fails
          allOrders = localOrderDataService.getAllOrders();
          setUseMongoData(false);
        }
      } else {
        // Get all orders from local JSON data
        allOrders = localOrderDataService.getAllOrders();
      }
      
      setOrders(allOrders);
      
      // Apply filters
      let filtered = [...allOrders];
      
      // Date filter
      // Mexico City is UTC-6 year-round (no DST since 2022). Anchor the day
      // boundaries to that fixed offset instead of the viewer's local
      // timezone (setHours() mutates local wall-clock time, so the same
      // filter used to silently cut off evening transactions depending on
      // where the browser viewing the dashboard happened to be).
      if (dateRange.from && dateRange.to) {
        const fromDate = new Date(`${dateRange.from}T00:00:00.000-06:00`);
        const toDate = new Date(`${dateRange.to}T23:59:59.999-06:00`);
        filtered = filtered.filter(order => {
          const orderDate = new Date(order.date);
          return orderDate >= fromDate && orderDate <= toDate;
        });
      }
      
      // Payment method filter
      if (paymentFilter !== 'all') {
        filtered = filtered.filter(order => 
          (order.paymentMethod || 'card') === paymentFilter
        );
      }
      
      // Employee discount filter
      if (employeeFilter === 'exclude') {
        filtered = filtered.filter(order => {
          const isEmployee = order.amount <= 0.05 ||
            order.products?.some(p => p.name.toLowerCase().includes('empleado') || p.name.toLowerCase().includes('cortesía'));
          return !isEmployee;
        });
      } else if (employeeFilter === 'only') {
        // Show only the cortesía/empleado line items from orders that contain them
        filtered = filtered
          .filter(order =>
            order.amount <= 0.05 ||
            order.products?.some(p => p.name.toLowerCase().includes('empleado') || p.name.toLowerCase().includes('cortesía'))
          )
          .map(order => {
            const employeeProducts = (order.products || []).filter(p =>
              p.name.toLowerCase().includes('empleado') || p.name.toLowerCase().includes('cortesía')
            );
            const employeeTotal = employeeProducts.reduce((sum, p) => sum + (p.total || p.price * p.quantity), 0);
            return {
              ...order,
              products: employeeProducts,
              amount: order.amount <= 0.05 ? order.amount : employeeTotal,
              hasDetailedData: employeeProducts.length > 0,
            };
          });
      }
      // employeeFilter === 'include' shows everything

      // Tip filter
      if (tipFilter === 'withTips') {
        filtered = filtered.filter(order => order.tip && order.tip > 0);
      } else if (tipFilter === 'onlyTips') {
        // Show only orders with tips, useful for tip reporting
        filtered = filtered.filter(order => order.tip && order.tip > 0);
      }

      // Search filter
      if (searchQuery) {
        filtered = filtered.filter(order => {
          const formatted = localOrderDataService.formatOrderForDisplay(order);
          return (
            formatted.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.orderNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            formatted.cashier.toLowerCase().includes(searchQuery.toLowerCase()) ||
            order.products?.some(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
          );
        });
      }
      
      // Sala de Juntas stats — computed independent of salaFilter so the
      // breakdown stays visible regardless of which sala filter is active
      const ticketsWithSala = filtered.filter(order =>
        order.products?.some(p => SALA_DE_JUNTAS_REGEX.test(p.name))
      );
      if (ticketsWithSala.length > 0) {
        let salaRevenue = 0;
        let otherRevenue = 0;
        let bookings = 0;
        let firstDate: string | null = null;
        ticketsWithSala.forEach(order => {
          if (!firstDate || new Date(order.date) < new Date(firstDate)) {
            firstDate = order.date;
          }
          (order.products || []).forEach(p => {
            if (SALA_DE_JUNTAS_REGEX.test(p.name)) {
              salaRevenue += p.total;
              bookings += p.quantity;
            } else {
              otherRevenue += p.total;
            }
          });
        });
        setSalaStats({ salaRevenue, otherRevenue, bookings, tickets: ticketsWithSala.length, firstDate });
      } else {
        setSalaStats(null);
      }

      // Sala de Juntas filter
      if (salaFilter === 'only') {
        filtered = ticketsWithSala;
      } else if (salaFilter === 'exclude') {
        filtered = filtered.filter(order => !order.products?.some(p => SALA_DE_JUNTAS_REGEX.test(p.name)));
      }

      setFilteredOrders(filtered);

      // Calculate summary
      const stats = localOrderDataService.getSummaryStats(filtered);
      setSummary(stats);

    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange, paymentFilter, searchQuery, useMongoData, employeeFilter, tipFilter, salaFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleRefresh = () => {
    fetchOrders();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getPaymentIcon = (method?: string) => {
    if (method === 'cash') {
      return <CashIcon size={16} className="text-green-600" />;
    }
    return <CardIcon size={16} className="text-blue-600" />;
  };

  const toggleProductDetails = (orderNumber: string) => {
    setShowProducts(showProducts === orderNumber ? null : orderNumber);
  };

  const setDatePreset = (preset: 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth') => {
    const today = new Date();
    let from: Date;
    let to: Date;

    switch (preset) {
      case 'thisWeek':
        from = startOfWeek(today, { weekStartsOn: 1 });
        to = endOfWeek(today, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        const lastWeek = subWeeks(today, 1);
        from = startOfWeek(lastWeek, { weekStartsOn: 1 });
        to = endOfWeek(lastWeek, { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        from = startOfMonth(today);
        to = endOfMonth(today);
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        from = startOfMonth(lastMonth);
        to = endOfMonth(lastMonth);
        break;
    }

    setDateRange({
      from: format(from, 'yyyy-MM-dd'),
      to: format(to, 'yyyy-MM-dd')
    });
  };

  // Handle product extraction for date range
  const handleExtractProducts = async () => {
    if (!dateRange.from || !dateRange.to) {
      alert('Por favor selecciona un rango de fechas primero');
      return;
    }
    
    setExtractingProducts(true);
    try {
      const result = await apiService.triggerProductExtraction(dateRange.from, dateRange.to);
      alert(`Pipeline iniciado para extraer productos del ${dateRange.from} al ${dateRange.to}. Esto tomará unos minutos.`);
      console.log('Pipeline URL:', result.pipelineUrl);
    } catch (error) {
      console.error('Error triggering product extraction:', error);
      alert('Error al iniciar la extracción de productos. Verifica la configuración.');
    } finally {
      setExtractingProducts(false);
    }
  };

  const handleProcessDeductions = async () => {
    if (!dateRange.from || !dateRange.to) {
      alert('Por favor selecciona un rango de fechas primero');
      return;
    }

    if (!window.confirm(`Procesar deducciones de inventario para ventas del ${dateRange.from} al ${dateRange.to}?`)) {
      return;
    }

    setProcessingDeductions(true);
    setDeductionResult(null);
    try {
      // Gather all products from filtered orders that have product data
      const ordersWithProducts = filteredOrders.filter(o => o.hasDetailedData && o.products?.length > 0);

      for (const order of ordersWithProducts) {
        const products = order.products.map(p => ({ name: p.name, quantity: p.quantity }));
        const ref = order.orderNumber || '';
        try {
          const result = await apiService.deductInventory(products, ref);
          setDeductionResult((prev: any) => {
            if (!prev) return result.summary;
            return {
              totalProducts: (prev.totalProducts || 0) + (result.summary?.totalProducts || 0),
              deductionsCount: (prev.deductionsCount || 0) + (result.summary?.deductionsCount || 0),
              skippedCount: (prev.skippedCount || 0) + (result.summary?.skippedCount || 0),
              newAlertsCount: (prev.newAlertsCount || 0) + (result.summary?.newAlertsCount || 0),
            };
          });
        } catch (err) {
          console.error('Error processing order:', ref, err);
        }
      }

      alert(`Ventas procesadas: ${ordersWithProducts.length} ordenes`);
    } catch (error) {
      console.error('Error processing deductions:', error);
      alert('Error al procesar deducciones');
    } finally {
      setProcessingDeductions(false);
    }
  };

  const handleExportCSV = () => {
    const rows = filteredOrders.map(order => {
      const formatted = localOrderDataService.formatOrderForDisplay(order);
      const productsText = order.products?.map(p => `${p.quantity}x ${p.name}`).join('; ') || '';
      return [
        formatted.formattedDate,
        formatted.formattedTime,
        order.orderNumber,
        formatted.paymentMethodDisplay,
        order.amount.toFixed(2),
        (order.tip || 0).toFixed(2),
        productsText,
      ];
    });

    const header = ['Fecha', 'Hora', 'Orden', 'Pago', 'Monto', 'Propina', 'Productos'];
    const csv = [header, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateLabel = dateRange.from && dateRange.to ? `${dateRange.from}_${dateRange.to}` : 'all';
    a.download = `ventas_${tipFilter === 'withTips' ? 'propinas_' : ''}${dateLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h2 className="text-2xl font-bold text-dark-text font-heading mb-4 md:mb-0">
          Ventas Clip - {useMongoData ? 'MongoDB' : 'Datos Locales'}
        </h2>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-medium-gray">
            {filteredOrders.length} de {orders.length} órdenes
          </span>
          <button
            onClick={() => setUseMongoData(!useMongoData)}
            className="text-xs px-2 py-1 bg-accent/10 hover:bg-accent/20 rounded text-warm-brown"
          >
            {useMongoData ? 'Usar JSON' : 'Usar MongoDB'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-4 py-2 bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors disabled:opacity-50"
          >
            <RefreshCw size={20} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20 mb-6">
        <div className="space-y-4">
          {/* Row 1: Search */}
          <div className="relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray" />
            <input
              type="text"
              placeholder="Buscar por referencia, orden, cajero..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
            />
          </div>

          {/* Row 2: Date Range and Payment Method */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* Date Range */}
            <div className="lg:col-span-2">
              <label className="block text-xs text-medium-gray mb-1">Rango de Fechas</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                  placeholder="Fecha desde"
                  className="flex-1 px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
                <span className="text-medium-gray flex-shrink-0">—</span>
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                  placeholder="Fecha hasta"
                  className="flex-1 px-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
                />
              </div>
              {/* Date Presets */}
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={() => setDatePreset('thisWeek')}
                  className="px-3 py-1 text-xs bg-cream border border-light-gray/30 text-dark-text rounded hover:bg-accent hover:text-cream transition-colors"
                >
                  Esta Semana
                </button>
                <button
                  onClick={() => setDatePreset('lastWeek')}
                  className="px-3 py-1 text-xs bg-cream border border-light-gray/30 text-dark-text rounded hover:bg-accent hover:text-cream transition-colors"
                >
                  Semana Pasada
                </button>
                <button
                  onClick={() => setDatePreset('thisMonth')}
                  className="px-3 py-1 text-xs bg-cream border border-light-gray/30 text-dark-text rounded hover:bg-accent hover:text-cream transition-colors"
                >
                  Este Mes
                </button>
                <button
                  onClick={() => setDatePreset('lastMonth')}
                  className="px-3 py-1 text-xs bg-cream border border-light-gray/30 text-dark-text rounded hover:bg-accent hover:text-cream transition-colors"
                >
                  Mes Pasado
                </button>
              </div>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className="block text-xs text-medium-gray mb-1">Método de Pago</label>
              <div className="relative">
                <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none z-10" />
                <select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value as 'all' | 'card' | 'cash')}
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                  className="w-full pl-10 pr-10 py-2 border border-light-gray/30 rounded-lg bg-white hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent transition-colors text-sm text-dark-text cursor-pointer"
                >
                  <option value="all">Todos</option>
                  <option value="card">Tarjeta</option>
                  <option value="cash">Efectivo</option>
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none" />
              </div>
            </div>

            {/* Employee Discount Filter */}
            <div>
              <label className="block text-xs text-medium-gray mb-1">Empleados/Cortesías</label>
              <div className="flex rounded-lg border border-light-gray/30 bg-white overflow-hidden">
                <button
                  onClick={() => setEmployeeFilter('exclude')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    employeeFilter === 'exclude'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Sin
                </button>
                <button
                  onClick={() => setEmployeeFilter('include')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    employeeFilter === 'include'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Todos
                </button>
                <button
                  onClick={() => setEmployeeFilter('only')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    employeeFilter === 'only'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Solo
                </button>
              </div>
            </div>

            {/* Propinas Filter */}
            <div>
              <label className="block text-xs text-medium-gray mb-1">Propinas</label>
              <div className="flex rounded-lg border border-light-gray/30 bg-white overflow-hidden">
                <button
                  onClick={() => setTipFilter('all')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    tipFilter === 'all'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setTipFilter('withTips')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    tipFilter === 'withTips'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Con Propina
                </button>
              </div>
            </div>

            {/* Sala de Juntas Filter */}
            <div>
              <label className="block text-xs text-medium-gray mb-1">Sala de Juntas</label>
              <div className="flex rounded-lg border border-light-gray/30 bg-white overflow-hidden">
                <button
                  onClick={() => setSalaFilter('all')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    salaFilter === 'all'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setSalaFilter('only')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    salaFilter === 'only'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Solo Sala
                </button>
                <button
                  onClick={() => setSalaFilter('exclude')}
                  className={`flex-1 px-3 py-2 text-xs transition-colors ${
                    salaFilter === 'exclude'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Sin Sala
                </button>
              </div>
            </div>
          </div>

          {/* Row 3: Action Buttons */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              {/* Extract Products Button */}
              {dateRange.from && dateRange.to && (
                <button
                  onClick={handleExtractProducts}
                  disabled={extractingProducts}
                  className="flex items-center px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Download size={16} className="mr-2" />
                  {extractingProducts ? 'Extrayendo...' : 'Extraer Productos'}
                </button>
              )}
              {/* Process Deductions Button */}
              {dateRange.from && dateRange.to && (
                <button
                  onClick={handleProcessDeductions}
                  disabled={processingDeductions}
                  className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <Package size={16} className="mr-2" />
                  {processingDeductions ? 'Procesando...' : 'Procesar Ventas'}
                </button>
              )}
            </div>

            {/* Export CSV Button */}
            {filteredOrders.length > 0 && (
              <button
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 text-sm bg-warm-brown text-cream rounded-lg hover:bg-dark-text transition-colors"
              >
                <Download size={16} className="mr-2" />
                Exportar CSV ({filteredOrders.length})
              </button>
            )}

            {/* Clear Filters Button */}
            <button
              onClick={() => {
                setDateRange({ from: '', to: '' });
                setPaymentFilter('all');
                setSearchQuery('');
                setEmployeeFilter('exclude');
                setTipFilter('all');
                setSalaFilter('all');
              }}
              className="px-4 py-2 text-sm text-warm-brown border border-warm-brown rounded-lg hover:bg-warm-brown hover:text-cream transition-colors ml-auto"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Total Ventas</p>
                <p className="text-xl lg:text-2xl font-bold text-dark-text truncate">
                  {formatCurrency(summary.totalAmount)}
                </p>
              </div>
              <DollarSign className="text-green-600 flex-shrink-0 ml-3" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Órdenes</p>
                <p className="text-xl lg:text-2xl font-bold text-dark-text">
                  {summary.totalOrders}
                </p>
              </div>
              <CreditCard className="text-accent flex-shrink-0 ml-3" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Ticket Promedio</p>
                <p className="text-xl lg:text-2xl font-bold text-dark-text truncate">
                  {formatCurrency(summary.averageTicket)}
                </p>
              </div>
              <TrendingUp className="text-warm-brown flex-shrink-0 ml-3" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Con Productos</p>
                <p className="text-xl lg:text-2xl font-bold text-dark-text">
                  {summary.ordersWithProducts}
                </p>
                <p className="text-xs text-medium-gray">
                  {summary.totalOrders > 0 ? ((summary.ordersWithProducts / summary.totalOrders) * 100).toFixed(0) : 0}%
                </p>
              </div>
              <Package className="text-blue-600 flex-shrink-0 ml-3" size={24} />
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Métodos de Pago</p>
                <div className="flex items-center space-x-2 mt-1">
                  {summary.paymentMethods.card && (
                    <span className="text-sm">
                      💳 {summary.paymentMethods.card.count}
                    </span>
                  )}
                  {summary.paymentMethods.cash && (
                    <span className="text-sm">
                      💵 {summary.paymentMethods.cash.count}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className={`bg-white rounded-lg p-4 shadow-sm border ${summary.totalTips > 0 ? 'border-pink-200' : 'border-light-gray/20'}`}>
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-medium-gray">Propinas</p>
                <p className="text-xl lg:text-2xl font-bold text-dark-text truncate">
                  {formatCurrency(summary.totalTips || 0)}
                </p>
                <p className="text-xs text-medium-gray">
                  {summary.ordersWithTips || 0} órdenes con propina
                </p>
              </div>
              <Heart className="text-pink-500 flex-shrink-0 ml-3" size={24} />
            </div>
          </div>
        </div>
      )}

      {/* Sala de Juntas Analysis */}
      {salaStats && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-accent/30 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2">
            <h3 className="text-lg font-semibold text-dark-text font-heading flex items-center">
              <Users size={20} className="mr-2 text-accent" />
              Sala de Juntas
            </h3>
            {salaStats.firstDate && (
              <span className="text-xs text-medium-gray">
                Primera reserva: {format(new Date(salaStats.firstDate), 'dd/MM/yyyy')}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <p className="text-sm text-medium-gray">Ingresos Sala</p>
              <p className="text-xl font-bold text-dark-text">{formatCurrency(salaStats.salaRevenue)}</p>
            </div>
            <div>
              <p className="text-sm text-medium-gray">Reservas</p>
              <p className="text-xl font-bold text-dark-text">{salaStats.bookings}</p>
            </div>
            <div>
              <p className="text-sm text-medium-gray">Tickets con Sala</p>
              <p className="text-xl font-bold text-dark-text">{salaStats.tickets}</p>
            </div>
            <div>
              <p className="text-sm text-medium-gray">Consumo Adicional</p>
              <p className="text-xl font-bold text-dark-text">{formatCurrency(salaStats.otherRevenue)}</p>
            </div>
            <div>
              <p className="text-sm text-medium-gray">Consumo Prom./Ticket</p>
              <p className="text-xl font-bold text-dark-text">
                {formatCurrency(salaStats.tickets > 0 ? salaStats.otherRevenue / salaStats.tickets : 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Deduction Result */}
      {deductionResult && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h4 className="font-semibold text-blue-800 mb-2">Resultado de Procesamiento</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-blue-600">Productos procesados:</span>
              <span className="ml-1 font-medium">{deductionResult.totalProducts}</span>
            </div>
            <div>
              <span className="text-blue-600">Deducciones:</span>
              <span className="ml-1 font-medium">{deductionResult.deductionsCount}</span>
            </div>
            <div>
              <span className="text-blue-600">Sin receta:</span>
              <span className="ml-1 font-medium">{deductionResult.skippedCount}</span>
            </div>
            <div>
              <span className="text-blue-600">Nuevas alertas:</span>
              <span className="ml-1 font-medium">{deductionResult.newAlertsCount}</span>
            </div>
          </div>
          <button
            onClick={() => setDeductionResult(null)}
            className="mt-2 text-xs text-blue-600 hover:text-blue-800"
          >
            Cerrar
          </button>
        </div>
      )}

      {/* Top Products */}
      {summary && summary.productsBreakdown && Object.keys(summary.productsBreakdown).length > 0 && (() => {
        const filteredProducts = Object.entries(summary.productsBreakdown)
          .filter(([product]: [string, any]) => {
            if (categoryFilter === 'all') return true;
            const productCategory = getProductCategory(product);
            return productCategory === categoryFilter;
          })
          .sort(([, a]: any, [, b]: any) => {
            if (sortBy === 'revenue') {
              return b.revenue - a.revenue;
            } else {
              return b.quantity - a.quantity;
            }
          });

        const hours = Array.from({ length: 13 }, (_, i) => i + 8);
        const ordersByHour: Record<number, number> = {};
        
        hours.forEach(hour => {
          ordersByHour[hour] = 0;
        });

        const ordersToAnalyze = categoryFilter === 'all' ? filteredOrders : filteredOrders.filter(order => {
          if (!order.hasDetailedData || !order.products) return false;
          return order.products.some(p => {
            const productCategory = getProductCategory(p.name);
            return productCategory === categoryFilter;
          });
        });

        const ordersOutsideRange: any[] = [];
        
        ordersToAnalyze.forEach(order => {
          try {
            const orderDate = new Date(order.date);
            const hour = orderDate.getHours();
            if (hour >= 8 && hour <= 20) {
              ordersByHour[hour] = (ordersByHour[hour] || 0) + 1;
            } else {
              ordersOutsideRange.push({ 
                orderNumber: order.orderNumber, 
                date: order.date, 
                hour,
                dateTimeText: order.dateTimeText 
              });
            }
          } catch (e) {
            console.error('Error parsing date:', e);
          }
        });

        const maxOrders = Math.max(...Object.values(ordersByHour), 1);
        const totalOrdersInGraph = Object.values(ordersByHour).reduce((a, b) => a + b, 0);

        console.log('Hourly Distribution:', { 
          ordersByHour, 
          maxOrders, 
          totalOrdersInGraph,
          totalFilteredOrders: filteredOrders.length,
          ordersAnalyzed: ordersToAnalyze.length,
          categoryFilter,
          ordersOutsideRange: ordersOutsideRange.length,
          sampleOutsideOrders: ordersOutsideRange.slice(0, 5)
        });

        const hourlyData = hours.map(hour => ({
          hour,
          label: `${hour}:00`,
          orders: ordersByHour[hour] || 0,
          percentage: maxOrders > 0 ? ((ordersByHour[hour] || 0) / maxOrders) * 100 : 0
        }));

        return (
          <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
              <h3 className="text-lg font-semibold text-dark-text font-heading">
                Productos Más Vendidos
              </h3>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative w-full sm:w-64">
                <Filter size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none z-10" />
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as ProductCategory | 'all')}
                  style={{
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                  className="w-full pl-10 pr-10 py-2 border border-light-gray/30 rounded-lg bg-white hover:border-accent focus:outline-none focus:ring-2 focus:ring-accent transition-colors text-sm text-dark-text cursor-pointer"
                >
                  <option value="all">Todas las categorías</option>
                  {getAllCategories().map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <ChevronDown size={18} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none" />
              </div>
              <div className="flex rounded-lg border border-light-gray/30 bg-white overflow-hidden">
                <button
                  onClick={() => setSortBy('revenue')}
                  className={`px-4 py-2 text-sm transition-colors ${
                    sortBy === 'revenue'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Total
                </button>
                <button
                  onClick={() => setSortBy('quantity')}
                  className={`px-4 py-2 text-sm transition-colors ${
                    sortBy === 'quantity'
                      ? 'bg-accent text-cream font-medium'
                      : 'bg-white text-dark-text hover:bg-cream'
                  }`}
                >
                  Unidades
                </button>
              </div>
            </div>
          </div>

          {/* Hourly Distribution Chart */}
          <div className="mb-6">
            <h4 className="text-md font-semibold text-dark-text mb-3">
              Distribución de Órdenes por Hora (8am - 8pm)
            </h4>
            <div className="flex items-end justify-between gap-2 h-48 bg-cream/20 rounded-lg p-4">
              {hourlyData.map(({ hour, label, orders, percentage }) => (
                <div key={hour} className="flex-1 flex flex-col items-center h-full">
                  <div className="relative w-full h-full flex items-end justify-center">
                    {orders > 0 && (
                      <div 
                        className="w-full bg-accent rounded-t transition-all duration-300 hover:bg-warm-brown relative group min-h-[4px]"
                        style={{ height: `${percentage}%`, minHeight: orders > 0 ? '8px' : '0px' }}
                      >
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-dark-text text-cream text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                          {orders} órdenes
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-medium-gray mt-2 whitespace-nowrap">
                    {hour}h
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <div className="flex gap-6 pb-2">
              {(() => {
                const pages = [];
                for (let i = 0; i < filteredProducts.length; i += 9) {
                  pages.push(filteredProducts.slice(i, i + 9));
                }
                
                return pages.map((pageProducts, pageIndex) => (
                  <div key={pageIndex} className="flex-shrink-0 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pageProducts.map(([product, data]: [string, any]) => (
                      <div key={product} className="flex items-center justify-between p-3 bg-cream/30 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-dark-text truncate">{product}</div>
                          <div className="text-xs text-medium-gray">
                            {getProductCategory(product)}
                          </div>
                          <div className="text-xs text-medium-gray">
                            {data.quantity} unidades • {data.orders} órdenes
                          </div>
                        </div>
                        <div className="text-right ml-3 flex-shrink-0">
                          <div className="text-sm font-medium text-dark-text">
                            {formatCurrency(data.revenue)}
                          </div>
                          <div className="text-xs text-medium-gray">
                            @ {formatCurrency(data.avgPrice)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow-sm border border-light-gray/20 overflow-hidden mb-6">
        <div className="px-6 py-4 border-b border-light-gray/20">
          <h3 className="text-lg font-semibold text-dark-text font-heading">
            Órdenes Detalladas
          </h3>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
            <p className="mt-4 text-medium-gray">Cargando órdenes...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-light-gray/20">
              <thead className="bg-cream">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Fecha/Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Orden
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Referencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Cajero
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Pago
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Monto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Propina
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-medium-gray uppercase tracking-wider">
                    Productos
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-light-gray/20">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-medium-gray">
                      No hay órdenes para los filtros seleccionados
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => {
                    const formatted = localOrderDataService.formatOrderForDisplay(order);
                    return (
                      <React.Fragment key={`${order.orderNumber}-${order.index}`}>
                        <tr className="hover:bg-cream/30 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text">
                            <div className="flex flex-col">
                              <span className="font-medium">{formatted.formattedDate}</span>
                              <span className="text-xs text-medium-gray flex items-center">
                                <Clock size={12} className="mr-1" />
                                {formatted.formattedTime}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-dark-text">
                              #{order.orderNumber}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-dark-text">
                            <span className="font-medium">
                              {formatted.displayName}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-medium-gray">
                            {formatted.cashier}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getPaymentIcon(order.paymentMethod)}
                              <span className="ml-2 text-sm text-dark-text">
                                {formatted.paymentMethodDisplay}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-text">
                            {formatCurrency(order.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-text">
                            {order.tip && order.tip > 0 ? (
                              <span className="text-pink-600 font-medium">{formatCurrency(order.tip)}</span>
                            ) : (
                              <span className="text-medium-gray">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {order.hasDetailedData ? (
                              <button
                                onClick={() => toggleProductDetails(order.orderNumber)}
                                className="flex items-center text-sm text-accent hover:text-warm-brown transition-colors"
                              >
                                <Package size={16} className="mr-1" />
                                {order.products.length} productos
                              </button>
                            ) : (
                              <span className="text-sm text-medium-gray">-</span>
                            )}
                          </td>
                        </tr>
                        {/* Product Details Row */}
                        {showProducts === order.orderNumber && order.hasDetailedData && (
                          <tr>
                            <td colSpan={8} className="px-6 py-4 bg-cream/20">
                              <div className="space-y-2">
                                <div className="text-sm font-medium text-dark-text mb-2">
                                  Detalle de Productos:
                                </div>
                                {order.products.map((product, idx) => (
                                  <div key={idx} className="flex justify-between items-center text-sm">
                                    <div className="flex items-center">
                                      <span className="text-medium-gray mr-2">{product.quantity}x</span>
                                      <span className="text-dark-text">{product.name}</span>
                                    </div>
                                    <div className="flex items-center space-x-4">
                                      <span className="text-medium-gray">
                                        @ {formatCurrency(product.price)}
                                      </span>
                                      <span className="font-medium text-dark-text">
                                        {formatCurrency(product.total)}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                                <div className="border-t border-light-gray/30 pt-2 mt-2">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium text-dark-text">Total:</span>
                                    <span className="text-sm font-bold text-accent">
                                      {formatCurrency(order.amount)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClipTransactions;